import { Link } from 'react-router-dom'
import Logo from './Logo'
import { appSignupUrl } from '@/utils/signupUrl'

const footerLinks = {
  product: [
    { label: 'Product', href: '/product' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Demo', href: '/demo' },
    { label: 'Sign Up', href: appSignupUrl() },
  ],
  company: [
    { label: 'About', href: '/about' },
    { label: 'Case Studies', href: '/case-studies' },
  ],
  resources: [
    { label: 'Resources', href: '/resources' },
    { label: 'Value of Advice', href: '/value-of-advice' },
    { label: 'Help Us Build', href: '/help-us-build' },
    { label: 'Security', href: '/security' },
  ],
  legal: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Service', href: '/terms' },
  ],
}

export default function Footer() {
  return (
    <footer className="w-full bg-slate-900 pt-20 pb-10 px-6">
      <div className="max-w-[1200px] mx-auto">
        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_2fr] gap-20 pb-15 border-b border-slate-700">
          {/* Brand Section */}
          <div>
            <Logo variant="dark" />
            <p className="text-[15px] text-slate-400 leading-relaxed mt-5 max-w-[300px]">
              AI-powered software that transforms how financial advisers work, every single day.
            </p>
            <p className="text-[13px] text-accent-tealLight font-medium mt-3 italic">
              Built by Advisers. Owned by Advisers.
            </p>

            {/* Social */}
            <div className="flex items-center gap-4 mt-5">
              <a href="https://www.linkedin.com/company/wealthspace" target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-white transition-colors" aria-label="LinkedIn">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              </a>
            </div>
          </div>

          {/* Links Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            <FooterColumn title="Product" links={footerLinks.product} />
            <FooterColumn title="Company" links={footerLinks.company} />
            <FooterColumn title="Resources" links={footerLinks.resources} />
            <FooterColumn title="Legal" links={footerLinks.legal} />
          </div>
        </div>

        {/* Bottom */}
        <div className="flex flex-col sm:flex-row justify-between items-center pt-[30px] gap-5">
          <p className="text-[13px] text-slate-500 text-center sm:text-left">
            &copy; {new Date().getFullYear()} WealthSpace Ltd (Company No. 15396707). Registered in
            England &amp; Wales. Made in the UK.
          </p>
        </div>
      </div>
    </footer>
  )
}

interface FooterColumnProps {
  title: string
  links: { label: string; href: string }[]
}

function FooterColumn({ title, links }: FooterColumnProps) {
  return (
    <div>
      <h4 className="text-[13px] font-semibold text-slate-400 uppercase tracking-[0.05em] mb-5">
        {title}
      </h4>
      {links.map((link) => {
        const external = link.href.startsWith('http')
        const className =
          'block text-sm text-slate-500 py-1.5 no-underline transition-colors duration-300 hover:text-white'
        return external ? (
          <a
            key={link.href}
            href={link.href}
            target="_blank"
            rel="noopener noreferrer"
            className={className}
          >
            {link.label}
          </a>
        ) : (
          <Link key={link.href} to={link.href} className={className}>
            {link.label}
          </Link>
        )
      })}
    </div>
  )
}
